/**
 * The simple reader: one 2D slice, base plus overlays, no WebGL.
 *
 * Modeled on OpenADS's own desktop GUI (`GUI_launcher.py`'s `NiftiViewer`):
 * percentile-clip the base image, pick a plane and a slice, draw it. That tool
 * uses plain matplotlib `imshow` rather than a 3D engine because a 2D read
 * does not need one — it should open instantly and never touch a GPU context.
 * This is that same idea on a `<canvas>`, extended from the Python tool's one
 * overlay to several: each checked file is composited in its own flat color so
 * more than one can be on screen without the reader losing track of which is
 * which (`GUI_launcher.py`'s `_load_clicked`, read 2026-10 — normalization
 * matches it: 1st/99th percentile clip over finite voxels, to [0, 1]).
 *
 * Orientation here favors "legible" over "pixel-identical to the Python tool":
 * axial/coronal keep superior and anterior toward the top, which is the usual
 * radiological reading convention, but no attempt is made to match matplotlib's
 * exact rotation. That was never a requirement — only the normalization was.
 */

import * as React from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import type { NiftiVolume } from "@/lib/nifti";
import { loadVolume } from "./volumeCache";

export type SliceOrientation = "axial" | "coronal" | "sagittal";

export const ORIENTATIONS: { id: SliceOrientation; label: string }[] = [
  { id: "axial", label: "Axial (Z)" },
  { id: "coronal", label: "Coronal (Y)" },
  { id: "sagittal", label: "Sagittal (X)" },
];

/** Distinct, legible flat colors for stacked overlays — cycled by selection order. */
export const OVERLAY_PALETTE: [number, number, number][] = [
  [255, 210, 0], // amber — the Python tool's single-overlay default (autumn colormap)
  [0, 225, 255], // cyan
  [255, 70, 140], // magenta-pink
  [120, 255, 90], // green
  [160, 120, 255], // violet
  [255, 140, 40], // orange
];

export interface SliceOverlaySpec {
  url: string;
  color: [number, number, number];
  label: string;
}

interface Loaded {
  state: "idle" | "loading" | "ready" | "error";
  message?: string;
}

/**
 * 1st/99th percentile clip to [0, 1] over finite voxels — exported so the
 * interactive segmentation panel (`tools/segment/`) can show the exact same
 * normalized read SAM/MedSAM run on, rather than a second, possibly-different
 * normalization of the same slice.
 */
export function percentileClip(vol: NiftiVolume): Float32Array {
  const { voxels } = vol;
  const finite: number[] = [];
  // Sampling rather than sorting every voxel: a 1mm MNI volume is ~7M voxels,
  // and the 1st/99th percentile of a few hundred thousand samples is the same
  // number to any precision this display needs.
  const stride = Math.max(1, Math.floor(voxels.length / 200_000));
  for (let i = 0; i < voxels.length; i += stride) {
    const v = voxels[i];
    if (Number.isFinite(v)) finite.push(v);
  }
  finite.sort((a, b) => a - b);
  const at = (p: number) => finite[Math.min(finite.length - 1, Math.max(0, Math.round(p * (finite.length - 1))))];
  const lo = finite.length ? at(0.01) : 0;
  const hi = finite.length ? at(0.99) : 1;
  const span = hi - lo || 1e-8;
  const out = new Float32Array(voxels.length);
  for (let i = 0; i < voxels.length; i++) {
    const v = voxels[i];
    out[i] = Number.isFinite(v) ? Math.min(1, Math.max(0, (v - lo) / span)) : 0;
  }
  return out;
}

/** Also used by `tools/segment/PromptCanvas` to find the same slice SAM sees. */
export function axisFor(orientation: SliceOrientation): 0 | 1 | 2 {
  return orientation === "sagittal" ? 0 : orientation === "coronal" ? 1 : 2;
}

/** The two in-plane voxel axes, and whether each should be flipped for display. */
export function planeLayout(orientation: SliceOrientation): { uAxis: 0 | 1; vAxis: 0 | 1 | 2; flipV: boolean } {
  // RAS: axis 0 = Right, 1 = Anterior, 2 = Superior. Keep Superior/Anterior up.
  if (orientation === "axial") return { uAxis: 0, vAxis: 1, flipV: false };
  if (orientation === "coronal") return { uAxis: 0, vAxis: 2, flipV: false };
  return { uAxis: 1, vAxis: 2, flipV: false };
}

/** Inverse of a row-major 4x4 affine (general, via cofactors). */
function invert4(m: ArrayLike<number>): Float64Array | null {
  const a = Array.from(m);
  const inv = new Float64Array(16);
  inv[0] = a[5] * a[10] * a[15] - a[5] * a[11] * a[14] - a[9] * a[6] * a[15] + a[9] * a[7] * a[14] + a[13] * a[6] * a[11] - a[13] * a[7] * a[10];
  inv[4] = -a[4] * a[10] * a[15] + a[4] * a[11] * a[14] + a[8] * a[6] * a[15] - a[8] * a[7] * a[14] - a[12] * a[6] * a[11] + a[12] * a[7] * a[10];
  inv[8] = a[4] * a[9] * a[15] - a[4] * a[11] * a[13] - a[8] * a[5] * a[15] + a[8] * a[7] * a[13] + a[12] * a[5] * a[11] - a[12] * a[7] * a[9];
  inv[12] = -a[4] * a[9] * a[14] + a[4] * a[10] * a[13] + a[8] * a[5] * a[14] - a[8] * a[6] * a[13] - a[12] * a[5] * a[10] + a[12] * a[6] * a[9];
  inv[1] = -a[1] * a[10] * a[15] + a[1] * a[11] * a[14] + a[9] * a[2] * a[15] - a[9] * a[3] * a[14] - a[13] * a[2] * a[11] + a[13] * a[3] * a[10];
  inv[5] = a[0] * a[10] * a[15] - a[0] * a[11] * a[14] - a[8] * a[2] * a[15] + a[8] * a[3] * a[14] + a[12] * a[2] * a[11] - a[12] * a[3] * a[10];
  inv[9] = -a[0] * a[9] * a[15] + a[0] * a[11] * a[13] + a[8] * a[1] * a[15] - a[8] * a[3] * a[13] - a[12] * a[1] * a[11] + a[12] * a[3] * a[9];
  inv[13] = a[0] * a[9] * a[14] - a[0] * a[10] * a[13] - a[8] * a[1] * a[14] + a[8] * a[2] * a[13] + a[12] * a[1] * a[10] - a[12] * a[2] * a[9];
  inv[2] = a[1] * a[6] * a[15] - a[1] * a[7] * a[14] - a[5] * a[2] * a[15] + a[5] * a[3] * a[14] + a[13] * a[2] * a[7] - a[13] * a[3] * a[6];
  inv[6] = -a[0] * a[6] * a[15] + a[0] * a[7] * a[14] + a[4] * a[2] * a[15] - a[4] * a[3] * a[14] - a[12] * a[2] * a[7] + a[12] * a[3] * a[6];
  inv[10] = a[0] * a[5] * a[15] - a[0] * a[7] * a[13] - a[4] * a[1] * a[15] + a[4] * a[3] * a[13] + a[12] * a[1] * a[7] - a[12] * a[3] * a[5];
  inv[14] = -a[0] * a[5] * a[14] + a[0] * a[6] * a[13] + a[4] * a[1] * a[14] - a[4] * a[2] * a[13] - a[12] * a[1] * a[6] + a[12] * a[2] * a[5];
  inv[3] = -a[1] * a[6] * a[11] + a[1] * a[7] * a[10] + a[5] * a[2] * a[11] - a[5] * a[3] * a[10] - a[9] * a[2] * a[7] + a[9] * a[3] * a[6];
  inv[7] = a[0] * a[6] * a[11] - a[0] * a[7] * a[10] - a[4] * a[2] * a[11] + a[4] * a[3] * a[10] + a[8] * a[2] * a[7] - a[8] * a[3] * a[6];
  inv[11] = -a[0] * a[5] * a[11] + a[0] * a[7] * a[9] + a[4] * a[1] * a[11] - a[4] * a[3] * a[9] - a[8] * a[1] * a[7] + a[8] * a[3] * a[5];
  inv[15] = a[0] * a[5] * a[10] - a[0] * a[6] * a[9] - a[4] * a[1] * a[10] + a[4] * a[2] * a[9] + a[8] * a[1] * a[6] - a[8] * a[2] * a[5];
  const det = a[0] * inv[0] + a[1] * inv[4] + a[2] * inv[8] + a[3] * inv[12];
  if (!det) return null;
  for (let i = 0; i < 16; i++) inv[i] /= det;
  return inv;
}

/**
 * Base voxel -> overlay voxel (row-major 4x4), so an overlay on another grid
 * (native HP over TTP, a mask over a resampled image) lands where it belongs
 * in world space. Null when the two grids are identical.
 */
function voxelMap(base: NiftiVolume, ov: NiftiVolume): Float64Array | null {
  const same =
    base.dims.every((d, i) => d === ov.dims[i]) && Array.from(base.affine).every((v, i) => Math.abs(v - ov.affine[i]) < 1e-3);
  if (same) return null;
  const inv = invert4(ov.affine);
  if (!inv) return null;
  const out = new Float64Array(16);
  for (let r = 0; r < 4; r++)
    for (let c = 0; c < 4; c++) {
      let v = 0;
      for (let k = 0; k < 4; k++) v += inv[r * 4 + k] * base.affine[k * 4 + c];
      out[r * 4 + c] = v;
    }
  return out;
}

function drawSlice(
  canvas: HTMLCanvasElement,
  base: { vol: NiftiVolume; norm: Float32Array } | null,
  overlays: { vol: NiftiVolume; color: [number, number, number] }[],
  orientation: SliceOrientation,
  index: number,
): void {
  if (!base) return;
  const { dims } = base.vol;
  const layout = planeLayout(orientation);
  const uDim = dims[layout.uAxis];
  const vDimAxis = orientation === "axial" ? 1 : 2;
  const vDim = dims[vDimAxis];
  // Voxels are drawn at their physical size (4 mm slices stay 4 mm tall), so
  // the picture is drawn once at voxel resolution and then stretched by the
  // voxel spacing onto the visible canvas.
  const pu = Math.abs(base.vol.pixdim[layout.uAxis] || 1);
  const pv = Math.abs(base.vol.pixdim[vDimAxis] || 1);
  const scale = 4 / Math.min(pu, pv);
  const src = document.createElement("canvas");
  src.width = uDim;
  src.height = vDim;
  const ctx = src.getContext("2d");
  if (!ctx) return;
  const img = ctx.createImageData(uDim, vDim);
  const [nx, ny] = dims;
  const voxIndex = (x: number, y: number, z: number) => x + y * nx + z * nx * ny;
  const maps = overlays.map((ov) => voxelMap(base.vol, ov.vol));

  for (let v = 0; v < vDim; v++) {
    const row = layout.flipV ? vDim - 1 - v : v;
    for (let u = 0; u < uDim; u++) {
      let x = 0;
      let y = 0;
      let z = 0;
      if (orientation === "axial") {
        x = u;
        y = row;
        z = index;
      } else if (orientation === "coronal") {
        x = u;
        y = index;
        z = row;
      } else {
        x = index;
        y = u;
        z = row;
      }
      const voxIdx = voxIndex(x, y, z);
      const g = Math.round((base.norm[voxIdx] ?? 0) * 255);
      // Image row 0 is the top; present-day convention here puts Superior and
      // Anterior at the top, so v increases downward in the destination while
      // the voxel axis increases upward — hence (vDim - 1 - v) below.
      const destRow = vDim - 1 - v;
      const pixel = (destRow * uDim + u) * 4;
      img.data[pixel] = g;
      img.data[pixel + 1] = g;
      img.data[pixel + 2] = g;
      img.data[pixel + 3] = 255;

      for (let oi = 0; oi < overlays.length; oi++) {
        const ov = overlays[oi];
        const m = maps[oi];
        let ovVal: number;
        if (!m) {
          if (ov.vol.dims[0] !== nx || ov.vol.dims[1] !== ny || ov.vol.dims[2] !== dims[2]) continue;
          ovVal = ov.vol.voxels[voxIdx];
        } else {
          const ox = Math.round(m[0] * x + m[1] * y + m[2] * z + m[3]);
          const oy = Math.round(m[4] * x + m[5] * y + m[6] * z + m[7]);
          const oz = Math.round(m[8] * x + m[9] * y + m[10] * z + m[11]);
          const [ux, uy, uz] = ov.vol.dims;
          if (ox < 0 || oy < 0 || oz < 0 || ox >= ux || oy >= uy || oz >= uz) continue;
          ovVal = ov.vol.voxels[ox + oy * ux + oz * ux * uy];
        }
        if (!ovVal || ovVal <= 0) continue;
        const alpha = 0.5;
        img.data[pixel] = img.data[pixel] * (1 - alpha) + ov.color[0] * alpha;
        img.data[pixel + 1] = img.data[pixel + 1] * (1 - alpha) + ov.color[1] * alpha;
        img.data[pixel + 2] = img.data[pixel + 2] * (1 - alpha) + ov.color[2] * alpha;
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  canvas.width = Math.round(uDim * pu * scale);
  canvas.height = Math.round(vDim * pv * scale);
  const out = canvas.getContext("2d");
  if (!out) return;
  out.imageSmoothingEnabled = false;
  out.drawImage(src, 0, 0, canvas.width, canvas.height);
}

export function SliceCanvas({
  baseUrl,
  overlays,
  className,
}: {
  baseUrl: string | null;
  overlays: SliceOverlaySpec[];
  className?: string;
}) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [orientation, setOrientation] = React.useState<SliceOrientation>("axial");
  const [index, setIndex] = React.useState(0);
  const [baseState, setBaseState] = React.useState<Loaded>({ state: "idle" });
  const baseRef = React.useRef<{ vol: NiftiVolume; norm: Float32Array } | null>(null);
  const overlaysRef = React.useRef<{ vol: NiftiVolume; color: [number, number, number] }[]>([]);
  const [overlaysReady, setOverlaysReady] = React.useState(0);

  // Load (and normalize) the base volume whenever the URL changes.
  React.useEffect(() => {
    baseRef.current = null;
    setIndex(0);
    if (!baseUrl) {
      setBaseState({ state: "idle" });
      return;
    }
    let live = true;
    setBaseState({ state: "loading" });
    loadVolume(baseUrl)
      .then((vol) => {
        if (!live) return;
        baseRef.current = { vol, norm: percentileClip(vol) };
        const axis = axisFor(orientation);
        setIndex(Math.floor(vol.dims[axis] / 2));
        setBaseState({ state: "ready" });
      })
      .catch((e) => {
        if (live) setBaseState({ state: "error", message: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      live = false;
    };
    // orientation intentionally excluded: switching plane should not reload
  }, [baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load whichever overlays are checked.
  React.useEffect(() => {
    let live = true;
    setOverlaysReady((n) => n); // no-op, keeps lint calm about the dep below
    Promise.all(
      overlays.map(async (ov) => ({ vol: await loadVolume(ov.url), color: ov.color })),
    )
      .then((loaded) => {
        if (!live) return;
        overlaysRef.current = loaded;
        setOverlaysReady((n) => n + 1);
      })
      .catch(() => {
        if (live) {
          overlaysRef.current = [];
          setOverlaysReady((n) => n + 1);
        }
      });
    return () => {
      live = false;
    };
  }, [overlays]);

  // Redraw whenever anything that affects the picture changes.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !baseRef.current) return;
    drawSlice(canvas, baseRef.current, overlaysRef.current, orientation, index);
  }, [orientation, index, baseState.state, overlaysReady]);

  const max = baseRef.current ? baseRef.current.vol.dims[axisFor(orientation)] - 1 : 0;

  const changeOrientation = (next: SliceOrientation) => {
    setOrientation(next);
    if (baseRef.current) setIndex(Math.floor(baseRef.current.vol.dims[axisFor(next)] / 2));
  };

  return (
    <div className={cn("flex h-full min-h-0 flex-col bg-ads-viewer", className)}>
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
        onWheel={(e) => {
          if (baseState.state !== "ready") return;
          setIndex((i) => Math.max(0, Math.min(max, i + (e.deltaY > 0 ? -1 : 1))));
        }}
      >
        {baseState.state === "loading" ? (
          <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
        ) : baseState.state === "error" ? (
          <p className="max-w-xs px-4 text-center text-[11px] text-red-300">{baseState.message}</p>
        ) : !baseUrl ? (
          <p className="text-[11px] text-neutral-500">Pick a base image to read.</p>
        ) : null}
        <canvas
          ref={canvasRef}
          className={cn(
            // Fill the pane, keeping the slice's aspect: the canvas holds the
            // native voxel grid and CSS scales it up.
            "h-full w-full object-contain [image-rendering:pixelated]",
            baseState.state !== "ready" && "hidden",
          )}
        />
      </div>
      <div className="flex shrink-0 flex-col gap-1 border-t border-ads-border bg-ads-surface px-3 py-1.5">
        <div className="flex items-center gap-1">
          {ORIENTATIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => changeOrientation(o.id)}
              className={cn(
                "rounded px-2 py-0.5 text-[11px] transition",
                o.id === orientation
                  ? "bg-ads-accent-soft font-medium text-ads-accent"
                  : "text-neutral-500 hover:bg-ads-hover",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pb-0.5">
          <input
            type="range"
            min={0}
            max={max}
            step={1}
            value={Math.min(index, max)}
            disabled={baseState.state !== "ready"}
            aria-label={`${orientation} slice`}
            onChange={(e) => setIndex(Number(e.target.value))}
            className="h-4 min-w-0 flex-1 cursor-pointer accent-ads-accent disabled:cursor-default disabled:opacity-40"
          />
          <span className="w-16 shrink-0 text-right text-[10.5px] tabular-nums text-neutral-500">
            {baseState.state === "ready" ? `${Math.min(index, max) + 1} / ${max + 1}` : "—"}
          </span>
        </div>
      </div>
    </div>
  );
}
